import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import { useAuth } from '../../context/AuthContext';
import { getRemainingGuestSessions, canStartGuestSession, recordGuestSession, getGuestResetTime, MAX_SESSIONS } from '../../utils/guestSessions';
import './PublicPages.css';
import './TryItFreePage.css';

export default function TryItFreePage() {
  const navigate = useNavigate();
  const { user, hasSession } = useAuth();
  const isLoggedIn = !!(user || hasSession);
  const [remaining, setRemaining] = useState(getRemainingGuestSessions());
  const [resetTime, setResetTime] = useState(getGuestResetTime());

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  function handleStartSession(mode) {
    if (isLoggedIn) {
      navigate('/preferences', { state: { mode } });
      return;
    }
    if (!canStartGuestSession()) {
      return; // limit reached, UI shows sign-up prompt
    }
    recordGuestSession();
    setRemaining(getRemainingGuestSessions());
    setResetTime(getGuestResetTime());
    navigate('/preferences', { state: { mode } });
  }

  const limitReached = !isLoggedIn && !canStartGuestSession();

  return (
    <div className="public-page try-it-page">
      <SEO
        title="Try Hangrily Free — Pick a Restaurant in 2 Minutes (No Sign-Up)"
        description="Try Hangrily right now — no account needed. Swipe through nearby restaurants solo, with your partner, or with friends. Pick where to eat in under 2 minutes. 3 free sessions per week."
        path="/try"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'Hangrily — Restaurant Decision App',
            description: 'The Tinder-style restaurant picker that helps you decide where to eat in under 2 minutes. Works solo, with a partner, or with a group of up to 10.',
            url: 'https://hangrily.app/try',
            applicationCategory: 'LifestyleApplication',
            operatingSystem: 'Web Browser, iOS, Android',
            browserRequirements: 'Requires JavaScript. Requires Geolocation.',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD',
              description: '3 free sessions per week. Unlimited with a free account.'
            },
            featureList: [
              'Solo mode — swipe through restaurants and pick your match',
              'Couple mode — both partners swipe independently and match on favorites',
              'Group mode — up to 10 people swipe and the consensus pick wins',
              'GPS-powered restaurant discovery via Google Places',
              'Cuisine, price, mood, and distance filters',
              'Real photos, ratings, and reviews for every restaurant',
              'Multi-round elimination algorithm for the best match',
              'Directions, delivery links, and restaurant details on results'
            ],
            screenshot: 'https://hangrily.app/og-image.png',
            softwareVersion: '2.3',
            author: { '@type': 'Organization', name: 'Hangrily', url: 'https://hangrily.app' }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Try Free', item: 'https://hangrily.app/try' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'How to Use Hangrily to Pick a Restaurant',
            description: 'Decide where to eat in under 2 minutes using Hangrily\'s swipe-based restaurant picker.',
            totalTime: 'PT2M',
            tool: { '@type': 'HowToTool', name: 'Hangrily Web App' },
            step: [
              { '@type': 'HowToStep', position: 1, name: 'Choose your mode', text: 'Pick Solo (just you), Couple (both partners swipe), or Group (up to 10 friends). No account needed for your first 3 sessions.' },
              { '@type': 'HowToStep', position: 2, name: 'Set your preferences', text: 'Select cuisine types, price range, mood, and distance. Allow location access so the app finds restaurants near you.' },
              { '@type': 'HowToStep', position: 3, name: 'Swipe through restaurants', text: 'See restaurant cards with real photos, ratings, and distance. Swipe right to like, left to pass, up to love.' },
              { '@type': 'HowToStep', position: 4, name: 'Get your match', text: 'After up to 3 rounds, the app picks the restaurant with the most agreement. Get directions, delivery links, or save it for later.' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Can I use Hangrily without creating an account?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes! You get 3 free sessions per week without signing up. Just tap a mode (Solo, Couple, or Group), set your preferences, and start swiping. No email, no password, no credit card. Create a free account anytime for unlimited sessions.'
                }
              },
              {
                '@type': 'Question',
                name: 'How does the restaurant picker work?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily uses your location to find nearby restaurants via Google Places. You set filters for cuisine, price, and mood. Then you swipe through restaurant cards — right to like, left to pass, up to love. The app narrows your options through up to 3 rounds and picks the winner. In couple or group mode, everyone swipes independently and the app finds the restaurant with the most agreement.'
                }
              },
              {
                '@type': 'Question',
                name: 'What is the difference between Solo, Couple, and Group mode?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Solo mode is just you — swipe and get your personal top pick. Couple mode lets two partners swipe independently without seeing each other\'s choices, then reveals mutual favorites. Group mode supports up to 10 people who all swipe on the same restaurants — the app finds the consensus favorite that the most people agreed on.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is Hangrily really free?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'You get 3 sessions per week completely free with no account. Creating a free account gives you unlimited sessions. Premium features like analytics, favorites, and rewards are available with an affordable subscription.'
                }
              },
              {
                '@type': 'Question',
                name: 'Does it work in my city?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily works anywhere with Google Places coverage — virtually every city worldwide. Just allow location access and the app finds restaurants near you, whether you are in New York, a small town, or traveling abroad.'
                }
              },
              {
                '@type': 'Question',
                name: 'How is this better than Google Maps or Yelp?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Google Maps and Yelp show you hundreds of options and leave you to decide. Hangrily curates restaurants based on your preferences and presents them one at a time as swipeable cards. It is a decision tool, not a discovery tool — especially powerful when deciding with a partner or group.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        {/* Hero */}
        <section className="try-hero">
          <div className="try-hero-inner">
            <div className="try-hero-badge">
              <span>🆓</span> No account needed — try it right now
            </div>
            <h1>Pick a Restaurant in Under 2 Minutes</h1>
            <p className="try-hero-sub">
              Swipe through nearby restaurants. Solo, with your partner, or with friends.
              {!isLoggedIn && <><br /><strong>{remaining} free session{remaining !== 1 ? 's' : ''} remaining this week.</strong></>}
            </p>
          </div>
        </section>

        {/* Mode Selector — the main CTA */}
        {!limitReached ? (
          <section className="try-modes">
            <div className="try-modes-grid">
              <button className="try-mode-card try-mode--solo" onClick={() => handleStartSession('solo')}>
                <span className="try-mode-emoji">🍽️</span>
                <h2>Solo</h2>
                <p>Just you. Swipe through restaurants and get your perfect match in 2 minutes.</p>
                <span className="try-mode-cta">Start Solo →</span>
              </button>

              <button className="try-mode-card try-mode--couple" onClick={() => handleStartSession('couple')}>
                <span className="try-mode-emoji">💑</span>
                <h2>Couple</h2>
                <p>Both partners swipe independently. Match on restaurants you both love. No more arguing.</p>
                <span className="try-mode-cta">Start Couple →</span>
              </button>

              <button className="try-mode-card try-mode--group" onClick={() => handleStartSession('group')}>
                <span className="try-mode-emoji">👥</span>
                <h2>Group</h2>
                <p>Up to 10 friends join via code. Everyone swipes, the consensus pick wins.</p>
                <span className="try-mode-cta">Start Group →</span>
              </button>
            </div>

            {!isLoggedIn && (
              <p className="try-sessions-note">
                ⏱️ {remaining} of {MAX_SESSIONS} free sessions left this week.{' '}
                <Link to="/register" className="try-sessions-link">Create a free account</Link> for unlimited.
              </p>
            )}
          </section>
        ) : (
          <section className="try-limit-reached">
            <div className="try-limit-card">
              <span className="try-limit-emoji">🔒</span>
              <h2>You've used your 3 free sessions this week</h2>
              <p>Create a free account to get <strong>unlimited sessions</strong> — plus favorites, analytics, partner linking, and rewards.</p>
              <div className="try-limit-actions">
                <button className="btn btn-primary btn-lg" onClick={() => navigate('/register')}>
                  Create Free Account →
                </button>
                <button className="btn btn-secondary btn-lg" onClick={() => navigate('/login')}>
                  Already have an account? Log in
                </button>
              </div>
              <p className="try-limit-reset">Free sessions reset in {resetTime}</p>
            </div>
          </section>
        )}

        {/* How It Works */}
        <section className="content-section">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-grid">
            <div className="step-card">
              <span className="step-number">1</span>
              <h3>Choose Your Mode</h3>
              <p>Pick Solo (just you), Couple (both partners swipe), or Group (up to 10 friends). No account needed for your first 3 sessions.</p>
            </div>
            <div className="step-card">
              <span className="step-number">2</span>
              <h3>Set Your Preferences</h3>
              <p>Select cuisine types, price range, mood, and distance. Allow location access so the app finds restaurants near you.</p>
            </div>
            <div className="step-card">
              <span className="step-number">3</span>
              <h3>Swipe Through Restaurants</h3>
              <p>See restaurant cards with real photos, ratings, and distance. Swipe right to like, left to pass, up to love.</p>
            </div>
            <div className="step-card">
              <span className="step-number">4</span>
              <h3>Get Your Match</h3>
              <p>After up to 3 rounds, the app picks the restaurant with the most agreement. Get directions, delivery links, or save it for later.</p>
            </div>
          </div>
        </section>

        {/* What Makes Hangrily Different */}
        <section className="content-section alt-bg">
          <h2 className="section-title">Why Hangrily Beats Other Restaurant Pickers</h2>
          <div className="reason-cards">
            <div className="reason-card">
              <div className="reason-icon">🎯</div>
              <h3>Preference-Based, Not Random</h3>
              <p>Unlike wheel spinners, Hangrily filters by cuisine, price, distance, and mood before you start swiping. Every option is one you might actually want.</p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">💑</div>
              <h3>Built for Couples & Groups</h3>
              <p>Everyone swipes independently — no peeking. The app finds the restaurant with the most agreement. It's democratic, fast, and nobody feels overruled.</p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">📸</div>
              <h3>Real Photos & Ratings</h3>
              <p>Every restaurant card shows Google-sourced photos, star ratings, price levels, distance, and cuisine type. No guessing.</p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">⚡</div>
              <h3>2-Minute Decisions</h3>
              <p>3 rounds of swiping, 15 restaurants max. The algorithm forces a decision. No endless scrolling, no analysis paralysis.</p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🌍</div>
              <h3>Works Anywhere</h3>
              <p>Powered by Google Places — works in any city worldwide. New York, small towns, traveling abroad. If it's on Google Maps, it's on Hangrily.</p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🆓</div>
              <h3>Free to Try</h3>
              <p>3 sessions per week with no account. Unlimited with a free sign-up. No credit card, no ads, no catch.</p>
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section className="content-section">
          <h2 className="section-title">Hangrily vs. The Alternatives</h2>
          <div className="comparison-grid">
            <div className="comparison-item lose">
              <h3>❌ Google Maps / Yelp</h3>
              <p>Shows 200+ options. No help deciding. 30+ minutes of scrolling and still no decision.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ Random Wheel Spinners</h3>
              <p>Ignores your preferences. Might land on a place you hate. Can't handle groups.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ "Where do you want to eat?"</h3>
              <p>"I don't care, you pick." "No, not that." The argument loop that never ends.</p>
            </div>
            <div className="comparison-item win">
              <h3>✅ Hangrily</h3>
              <p>Filtered by preferences. Everyone votes. Smart algorithm finds consensus. Done in 2 minutes. Zero arguments.</p>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="content-section alt-bg">
          <h2 className="section-title">Perfect for Every Situation</h2>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-icon">🍽️</span>
              <h3>Solo Dining</h3>
              <p>Can't decide? Solo mode cuts through decision fatigue in 2 minutes flat.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">💑</span>
              <h3>Date Night</h3>
              <p>End the "where do you want to eat" loop. Both swipe, both agree.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">👥</span>
              <h3>Friends & Family</h3>
              <p>Share a code, everyone swipes. The group's consensus pick wins democratically.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">💼</span>
              <h3>Team Lunch</h3>
              <p>Share the code on Slack. Everyone swipes at their desk. Decision before noon.</p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="content-section">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-list">
            <details className="faq-item">
              <summary>Can I use Hangrily without creating an account?</summary>
              <p>Yes! You get 3 free sessions per week without signing up. Just tap a mode (Solo, Couple, or Group), set your preferences, and start swiping. No email, no password, no credit card. Create a free account anytime for unlimited sessions.</p>
            </details>
            <details className="faq-item">
              <summary>How does the restaurant picker work?</summary>
              <p>Hangrily uses your location to find nearby restaurants via Google Places. You set filters for cuisine, price, and mood. Then you swipe through restaurant cards — right to like, left to pass, up to love. The app narrows your options through up to 3 rounds and picks the winner. In couple or group mode, everyone swipes independently and the app finds the restaurant with the most agreement.</p>
            </details>
            <details className="faq-item">
              <summary>What is the difference between Solo, Couple, and Group mode?</summary>
              <p>Solo mode is just you — swipe and get your personal top pick. Couple mode lets two partners swipe independently without seeing each other's choices, then reveals mutual favorites. Group mode supports up to 10 people who all swipe on the same restaurants — the app finds the consensus favorite that the most people agreed on.</p>
            </details>
            <details className="faq-item">
              <summary>Is Hangrily really free?</summary>
              <p>You get 3 sessions per week completely free with no account. Creating a free account gives you unlimited sessions. Premium features like analytics, favorites, and rewards are available with an affordable subscription.</p>
            </details>
            <details className="faq-item">
              <summary>Does it work in my city?</summary>
              <p>Hangrily works anywhere with Google Places coverage — virtually every city worldwide. Just allow location access and the app finds restaurants near you, whether you are in New York, a small town, or traveling abroad.</p>
            </details>
            <details className="faq-item">
              <summary>How is this better than Google Maps or Yelp?</summary>
              <p>Google Maps and Yelp show you hundreds of options and leave you to decide. Hangrily curates restaurants based on your preferences and presents them one at a time as swipeable cards. It is a decision tool, not a discovery tool — especially powerful when deciding with a partner or group.</p>
            </details>
          </div>
        </section>

        {/* Mid-page CTA */}
        {!limitReached ? (
          <section className="try-mid-cta">
            <h2>Ready? Pick a restaurant right now.</h2>
            <p>No sign-up required. Just tap and swipe.</p>
            <div className="try-mid-cta-buttons">
              <button className="btn btn-primary btn-lg" onClick={() => handleStartSession('solo')}>
                🍽️ Solo
              </button>
              <button className="btn btn-primary btn-lg" style={{ background: '#E64980' }} onClick={() => handleStartSession('couple')}>
                💑 Couple
              </button>
              <button className="btn btn-primary btn-lg" style={{ background: '#7950F2' }} onClick={() => handleStartSession('group')}>
                👥 Group
              </button>
            </div>
          </section>
        ) : (
          <section className="cta-banner">
            <h2>Want Unlimited Sessions?</h2>
            <p>Create a free account — takes 30 seconds. No credit card required.</p>
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/register')}>
              Create Free Account →
            </button>
          </section>
        )}

        {/* Internal Links */}
        <section className="content-section alt-bg">
          <div className="internal-links-section">
            <h2>Explore More</h2>
            <div className="internal-links-grid">
              <Link to="/where-should-i-eat" className="internal-link">Where Should I Eat?</Link>
              <Link to="/random-restaurant-picker" className="internal-link">Random Restaurant Picker</Link>
              <Link to="/date-night-restaurants" className="internal-link">Date Night Restaurants</Link>
              <Link to="/best-restaurants-near-me" className="internal-link">Best Restaurants Near Me</Link>
              <Link to="/what-to-eat-tonight" className="internal-link">What to Eat Tonight</Link>
              <Link to="/couples-restaurant-picker" className="internal-link">Couples Restaurant Picker</Link>
              <Link to="/group-restaurant-decision" className="internal-link">Group Restaurant Decision</Link>
              <Link to="/restaurant-wheel-alternative" className="internal-link">Restaurant Wheel Alternative</Link>
              <Link to="/locations" className="internal-link">Browse Cities</Link>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
